// src/components/Banners/NewBannerModal.js
import React, { useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { PhotoIcon } from "@heroicons/react/20/solid";
import { useDispatch, useSelector } from "react-redux";
import { createBanner } from "../../redux/slices/bannerSlice";
import { showNotification } from "../../redux/slices/notificationSlice";
import placeholderImage from "../../assets/images/placeholder.jpg";
import { useTranslation } from "../../contexts/LanguageContext";

function NewBannerModal({ isOpen, setIsOpen }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { banners = [] } = useSelector((state) => state.banners || {});
  const [formData, setFormData] = useState({
    image: null,
    imageBase64: null,
    sequenceNo: "",
    isActive: true,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [sequenceError, setSequenceError] = useState("");

  // Helper function to convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === "sequenceNo") {
      // Clear previous error
      setSequenceError("");
      
      // Check if sequence number already exists
      const sequenceExists = banners.some(banner => banner.sequenceNo === Number(value));
      if (sequenceExists && value) {
        setSequenceError(t("bannerForm.sequenceInUse").replace("{n}", value));
      }
    }
    
    setFormData((prev) => ({ 
      ...prev, 
      [name]: type === "checkbox" ? checked : value 
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const base64String = await convertToBase64(file);
        setFormData((prev) => ({ 
          ...prev, 
          image: file,
          imageBase64: base64String
        }));
        setImagePreview(URL.createObjectURL(file));
      } catch (error) {
        dispatch(showNotification({ message: t("bannerForm.errorProcessing"), type: "error" }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.imageBase64) {
      dispatch(showNotification({ message: t("bannerForm.selectImage"), type: "error" }));
      return;
    }

    // Check for duplicate sequence number before submitting
    const sequenceExists = banners.some(banner => banner.sequenceNo === Number(formData.sequenceNo));
    if (sequenceExists) {
      setSequenceError(t("bannerForm.sequenceInUse").replace("{n}", formData.sequenceNo));
      return;
    }

    // Send data as JSON with base64 image
    const dataToSend = {
      image: formData.imageBase64,
      sequenceNo: Number(formData.sequenceNo),
      isActive: formData.isActive,
    };

    dispatch(createBanner(dataToSend))
      .unwrap()
      .then(() => {
        dispatch(showNotification({ message: t("bannerForm.createdSuccess"), type: "success" }));
        setFormData({ image: null, imageBase64: null, sequenceNo: "", isActive: true });
        setImagePreview(null);
        setSequenceError("");
        setIsOpen(false);
      })
      .catch((error) => {
        dispatch(showNotification({ message: error || t("bannerForm.failedCreate"), type: "error" }));
      });
  };

  // Get available sequence numbers for suggestion
  const getAvailableSequenceNumbers = () => {
    if (!banners || !Array.isArray(banners)) {
      return Array.from({ length: 10 }, (_, i) => i + 1);
    }
    
    const usedSequenceNumbers = banners
      .filter(banner => banner && banner.sequenceNo) // Filter out null/undefined banners
      .map(banner => banner.sequenceNo);
    
    return Array.from({ length: 10 }, (_, i) => i + 1).filter(
      num => !usedSequenceNumbers.includes(num)
    );
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={setIsOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      {t("bannerForm.addTitle")}
                    </Dialog.Title>
                    <div className="mt-6">
                      <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Image Upload */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 text-left">
                            {t("bannerForm.image")}
                          </label>
                          <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
                            <div className="text-center">
                              {imagePreview ? (
                                <img
                                  src={imagePreview}
                                  alt="Banner preview"
                                  className="mx-auto h-32 w-32 object-cover rounded-lg"
                                />
                              ) : (
                                <PhotoIcon className="mx-auto h-12 w-12 text-gray-300" aria-hidden="true" />
                              )}
                              <div className="mt-4 flex text-sm leading-6 text-gray-600">
                                <label
                                  htmlFor="file-upload"
                                  className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                                >
                                  <span>{t("bannerForm.uploadFile")}</span>
                                  <input
                                    id="file-upload"
                                    name="file-upload"
                                    type="file"
                                    className="sr-only"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                  />
                                </label>
                                <p className="pl-1">{t("bannerForm.orDragDrop")}</p>
                              </div>
                              <p className="text-xs leading-5 text-gray-600">{t("bannerForm.fileHelp")}</p>
                            </div>
                          </div>
                        </div>

                        {/* Sequence Number */}
                        <div>
                          <label htmlFor="sequenceNo" className="block text-sm font-medium text-gray-700 text-left">
                            {t("bannerForm.sequenceNumber")}
                          </label>
                          <input
                            type="number"
                            name="sequenceNo"
                            id="sequenceNo"
                            value={formData.sequenceNo}
                            onChange={handleInputChange}
                            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                              sequenceError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                            }`}
                            placeholder={t("bannerForm.sequencePlaceholder")}
                            required
                          />
                          {sequenceError && (
                            <p className="mt-1 text-sm text-red-600 text-left">{sequenceError}</p>
                          )}
                          {getAvailableSequenceNumbers().length > 0 && (
                            <p className="mt-1 text-sm text-gray-500 text-left">
                              {t("bannerForm.availableNumbers")} {getAvailableSequenceNumbers().slice(0, 5).join(', ')}
                              {getAvailableSequenceNumbers().length > 5 && '...'}
                            </p>
                          )}
                        </div>

                        {/* Active Status */}
                        <div className="flex items-center justify-start">
                          <input
                            id="isActive"
                            name="isActive"
                            type="checkbox"
                            checked={formData.isActive}
                            onChange={handleInputChange}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                            {t("bannerForm.active")}
                          </label>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
                    onClick={handleSubmit}
                    disabled={!!sequenceError}
                  >
                    {t("bannerForm.addBanner")}
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                    onClick={() => setIsOpen(false)}
                  >
                    {t("bannerForm.cancel")}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default NewBannerModal;